import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiKey } from '../../utils/ktcUtils';
import Table from '../../components/Table';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

import { Pie } from 'react-chartjs-2';

import { API_URL, ENDPOINTS } from '../../constants/api';

export default function OrdersFeedback() {
  const [orders, setOrders] = useState([]);
  const [activeStatus, setActiveStatus] = useState('All');
  const [activeTime, setActiveTime] = useState('all');
  ChartJS.register(ArcElement, Tooltip, Legend);

  useEffect(() => {
    loadOrders();
  }, []);

  // =========================
  // LOAD DATA
  // =========================
  const loadOrders = async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      };

      const url = `${API_URL}/${ENDPOINTS.SYS}/getlink-apikey/pos-orders-feedback`;

      const res = await axios.get(url, { headers });

      const data = res.data?.report || [];
      console.log('data:', data);
      setOrders(data);

      // ✅ FIX: auto set first tab active
      const statuses = [...new Set(data.map((o) => o.order_type))];

      // if (statuses.length > 0) {
      //   setActiveStatus(statuses[0]); // FIRST TAB ACTIVE
      // }
    } catch (err) {
      console.error('Error loading orders:', err);
    }
  };

  // =========================
  // FILTERS
  // =========================
  const statuses = [
    'All',
    ...new Set(orders.map((o) => o.order_type).filter(Boolean)),
  ];

  const filteredOrders = orders
    .filter((o) => {
      const minutes = Number(o.minutes_taken || 0);

      // TIME FILTER
      if (activeTime === 'fast') return minutes <= 30;
      if (activeTime === 'normal') return minutes >= 31 && minutes <= 60;
      if (activeTime === 'late') return minutes >= 61;

      return true;
    })
    .filter((o) => {
      // STATUS FILTER (FIXED)
      if (!activeStatus || activeStatus === 'All') return true;

      return o.order_type === activeStatus;
    })
    .map((item, index) => ({
      'S/N': index + 1,
      ...item,
    }));

  const totalRows = filteredOrders.length;

  const totalAmount = filteredOrders.reduce(
    (sum, order) => sum + Number(order.total || 0),
    0
  );

  const avgOrder = totalAmount / totalRows;

  console.log('Total Rows:', totalRows);
  console.log('Total Amount:', totalAmount);
  // console.log('filtered:', filteredOrders)

  const statusCounts = orders.reduce((acc, order) => {
    const status = order.order_type || 'Unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const statusChartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        data: Object.values(statusCounts),
        backgroundColor: [
          '#4CAF50',
          '#2196F3',
          '#FF9800',
          '#F44336',
          '#9C27B0',
          '#009688',
          '#795548',
          '#607D8B',
        ],
        borderWidth: 1,
      },
    ],
  };

  const timeCounts = {
    '≤ 30 min': 0,
    '31–60 min': 0,
    '61+ min': 0,
  };

  orders.forEach((order) => {
    const minutes = Number(order.minutes_taken || 0);

    if (minutes <= 30) {
      timeCounts['≤ 30 min']++;
    } else if (minutes <= 60) {
      timeCounts['31–60 min']++;
    } else {
      timeCounts['61+ min']++;
    }
  });

  const timeChartData = {
    labels: Object.keys(timeCounts),
    datasets: [
      {
        data: Object.values(timeCounts),
        backgroundColor: [
          '#4CAF50', // ≤ 30 min
          '#FF9800', // 31–60 min
          '#F44336', // 61+ min
        ],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const value = context.raw;
            const percentage = ((value / total) * 100).toFixed(1);

            return `${context.label}: ${percentage}% (${value})`;
          },
        },
      },
    },
  };
  // =========================
  // UI
  // =========================
  return (
    <div className="  py-3">
      <h2 className="mb-3">📦 Orders Dashboard</h2>

      {/* MAIN TABS */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {statuses.map((status) => (
          <button
            key={status}
            onClick={() => setActiveStatus(status)}
            className={`btn btn-sm ${
              activeStatus === status ? 'btn-success' : 'btn-outline-secondary'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* SUB TABS */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          onClick={() => setActiveTime('all')}
          className={`btn btn-sm ${
            activeTime === 'all' ? 'btn-primary' : 'btn-outline-primary'
          }`}
        >
          📋 All
        </button>

        <button
          onClick={() => setActiveTime('fast')}
          className={`btn btn-sm ${
            activeTime === 'fast' ? 'btn-success' : 'btn-outline-success'
          }`}
        >
          ⚡ ≤ 30 min
        </button>

        <button
          onClick={() => setActiveTime('normal')}
          className={`btn btn-sm ${
            activeTime === 'normal' ? 'btn-warning' : 'btn-outline-warning'
          }`}
        >
          ⏱ 31–60 min
        </button>

        <button
          onClick={() => setActiveTime('late')}
          className={`btn btn-sm ${
            activeTime === 'late' ? 'btn-danger' : 'btn-outline-danger'
          }`}
        >
          🚨 61+ min
        </button>
      </div>

      {/* STATS CARDS */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <small className="text-muted">Total Orders</small>
              <h3>{totalRows}</h3>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm border-success">
            <div className="card-body">
              <small className="text-muted">Total Amount</small>
              <h3 className="text-success">${totalAmount.toFixed(2)}</h3>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm border-primary">
            <div className="card-body">
              <small className="text-muted">Average Order</small>
              <h3 className="text-primary">${avgOrder.toFixed(2)}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* TABLE + CHART GRID */}
      <div className="row g-3">
        {/* TABLE */}
        <div className="col-lg-10">
          <div className="card shadow-sm">
            <div className="card-body table-responsive">
              <Table tbodyData={filteredOrders} />
            </div>
          </div>
        </div>

        {/* CHARTS */}
        <div className="col-lg-2 d-flex flex-column gap-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6>Orders by Type (%)</h6>
              <Pie data={statusChartData} options={pieOptions} />
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <h6>Delivery Time (%)</h6>
              <Pie data={timeChartData} options={pieOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
